import type { ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import type { FlightFilterCabin } from '../results/filters/flight_filter_panel.js';
import { useExplorerEnv } from './explorer_env.js';
import type { ExplorerState } from './explorer_state.js';
import { returnSwitchesOf } from './explorer_steps.js';
import type { FlightExplorerOffer, FlightSearchParams } from './search_source.js';

/*
 * Os avisos da coluna de resultados do explorer: a re-busca que voltou ao quadro
 * anterior, o pedido que substitui o card multi, o rascunho da faixa, o confirm
 * travado e a volta que parte de outro aeroporto. Fora do `explorer_results.tsx`
 * pelo teto de linhas dele.
 */

export type ExplorerHintsProps = {
  replaceHint: 'trip' | 'trip_picks' | null;
  draftPending: boolean;
  confirmStuck: boolean;
};

/** O aviso recuperável da re-busca que falhou (#3213) ou pousou vazia (#6438): a
 *  cabine que se tentou e o gesto que a re-tenta. */
type ExplorerRollbackHint = {
  cabin: NonNullable<FlightSearchParams['cabin']> | undefined;
  policyFailed: boolean;
  /** A tentativa pousou sem voo (#6438) — repetir devolveria vazio. */
  empty: boolean;
  onRetry: (() => void) | undefined;
};

/**
 * A re-busca da perna ATIVA falhou e ela voltou ao quadro anterior (#3213). O
 * "Tentar de novo" passa pelo seam da cabine porque É o gesto que falhou: a
 * re-busca de uma perna com lista em cena só nasce da coluna de cortes, e o
 * pedido já reverteu a cabine — re-buscar pelo pedido buscaria a cabine velha.
 * Sem seam (congelado) o aviso fica, o botão não. A política do viajante que não
 * avaliou (#5376) também tira o botão: repetir falharia igual, e o aviso manda
 * falar com o administrador da empresa.
 */
export function rollbackHintOf(
  state: ExplorerState,
  cabin: FlightFilterCabin | undefined,
): ExplorerRollbackHint | null {
  const rollback = state.legRollback;
  if (!rollback || rollback.leg !== state.activeLeg) return null;
  const tried = rollback.params.cabin;
  const policyFailed = rollback.failure === 'policy_evaluation';
  const empty = rollback.empty === true;
  return {
    cabin: tried,
    policyFailed,
    empty,
    onRetry:
      cabin && tried && !policyFailed && !empty
        ? () => {
            cabin.onSearch(tried);
          }
        : undefined,
  };
}

function rollbackText(
  { cabin, policyFailed, empty }: ExplorerRollbackHint,
  copy: ReturnType<typeof useExplorerEnv>['copy'],
): string {
  if (policyFailed) return copy.page.researchKeptPolicy;
  if (!cabin) return copy.page.researchKept;
  const label = copy.filter.cabin[cabin];
  return empty ? copy.page.researchEmptyCabin(label) : copy.page.researchKeptCabin(label);
}

function RollbackHint(hint: ExplorerRollbackHint): ReactNode {
  const { copy } = useExplorerEnv();
  const { onRetry } = hint;
  return (
    <span className="fex-rollback-hint" role="alert">
      {rollbackText(hint, copy)}
      {onRetry && (
        <Button type="button" variant="link" size="sm" onClick={onRetry}>
          {copy.page.tryAgain}
        </Button>
      )}
    </span>
  );
}

export function ExplorerHints({
  replaceHint,
  draftPending,
  confirmStuck,
  rollback,
}: ExplorerHintsProps & { rollback: ExplorerRollbackHint | null }): ReactNode {
  const { copy } = useExplorerEnv();
  if (!replaceHint && !draftPending && !confirmStuck && !rollback) return null;
  return (
    <div className="fex-hints">
      {rollback && <RollbackHint {...rollback} />}
      {replaceHint && (
        <span className="fex-replace-hint" role="note">
          {replaceHint === 'trip_picks' ? copy.page.replacesTripDiscards : copy.page.replacesTrip}
        </span>
      )}
      {draftPending && (
        <span className="fex-draft-hint" role="note">
          {copy.page.draftPending}
        </span>
      )}
      {confirmStuck && (
        <span className="fex-stuck-hint" role="alert">
          {copy.page.confirmUnavailable}
        </span>
      )}
    </div>
  );
}

/**
 * O aviso no topo da FASE 2 (#4203): há voltas na lista partindo de outro
 * aeroporto que o da chegada da ida — e a pessoa não pediu a cidade inteira.
 * Nomeia a chegada da ida; cada linha diz o seu (`AirportSwitchTag`). A lista
 * não encolhe: a oferta do fornecedor é legítima, a escolha é que tem de ser
 * consciente.
 */
export function AirportSwitchHint({
  state,
  rows,
}: Readonly<{ state: ExplorerState; rows: FlightExplorerOffer[] }>): ReactNode {
  const { copy, airportSwitchOf } = useExplorerEnv();
  const switchOf = returnSwitchesOf(state, airportSwitchOf);
  const found = switchOf ? rows.map(switchOf).find((s) => s !== null && !s.expected) : undefined;
  if (!found) return null;
  return (
    <p
      className="fex-airport-hint m-0 rounded-lg bg-warning-soft px-3 py-2 text-ui text-warning-strong"
      role="note"
    >
      {copy.page.airportSwitchNotice(found.arrival)}
    </p>
  );
}
